import { useQueries, useQuery } from '@tanstack/react-query'
import { cacheKeys } from '../utils/queryKeyFactory'
import { useAuthenticatedUser } from '../providers/UserProvider'
import { cachedNotesContext } from '../contexts/CachedNotes'
import { useIsAuthenticated } from '@azure/msal-react'
import {
  fetchAvailableCalendars,
  fetchCalendarEvents,
} from '../lib/msal/graphService'
import { CalendarSource, ExternalCalendar } from '../utils/calendar'
import { DEFAULT_CALENDAR_COLOR } from '../utils/color'
import dayjs, { Dayjs } from 'dayjs'
import { CalendarEvent } from '../modules/calendar/CalendarEvent'
import { calculateEventPosition } from '../modules/calendar/timeline/utils/event'
import { Event } from '@microsoft/microsoft-graph-types'
import { isMsalEnabled } from '../lib/msal/authConfig'

export function useOutlookCalendars() {
  const user = useAuthenticatedUser()
  const isSignedIn = useIsAuthenticated()

  return useQuery({
    // Only enable if MSAL is enabled and user is signed in
    enabled: isMsalEnabled && isSignedIn,
    context: cachedNotesContext,
    staleTime: 0, // make this immediately stale so the following refetches will trigger
    refetchInterval: 5000 * 60, // every 5 minute
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 1, // once is enough
    queryKey: cacheKeys.outlookCalendars(user.userId),
    queryFn: fetchAvailableCalendars,
    select: (data) => {
      return data.map(
        (calendar) =>
          ({
            id: calendar.id,
            name: calendar.name,
            color:
              calendar.hexColor === ''
                ? DEFAULT_CALENDAR_COLOR
                : calendar.hexColor,
            source: CalendarSource.Outlook,
          }) as ExternalCalendar
      )
    },
  })
}

export function useOutlookEvents(selectedDay: Dayjs) {
  const user = useAuthenticatedUser()
  const isSignedIn = useIsAuthenticated()
  const day = selectedDay.format('YYYY-MM-DD') // stick to ISO 8601 default dayjs format for easier conversion
  const { data: calendars = [] } = useOutlookCalendars()

  return useQueries({
    context: cachedNotesContext,
    queries: calendars.map((calendar) => ({
      // Only enable if MSAL is enabled and user is signed in
      enabled: isMsalEnabled && isSignedIn,
      staleTime: 0, // make this immediately stale so the following refetches will trigger
      refetchInterval: 5000 * 60, // every 5 minute
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1, // once is enough
      queryKey: cacheKeys.outlookEvents(user.userId, calendar.id, day),
      queryFn: () => fetchCalendarEvents(calendar.id, day),
      select: (data: Event[]) => {
        return data
          .map((event) => {
            // Validate that we have valid start and end dates
            if (!event.start?.dateTime || !event.end?.dateTime) {
              return
            }

            const startDate = dayjs(event.start.dateTime)
            const endDate = dayjs(event.end.dateTime)

            // Validate that dayjs objects are valid
            if (!startDate.isValid() || !endDate.isValid()) {
              return
            }

            return {
              id: event.id,
              contentType: 'event',
              content: event.subject,
              startDate,
              endDate,
              position: calculateEventPosition(startDate, endDate, selectedDay),
              allDay: event.isAllDay,
              description: event.body?.content,
              color: calendar.color,
              calendarId: calendar.id,
              calendarName: calendar.name,
            } as CalendarEvent
          })
          .filter(Boolean) // Remove null entries
      },
    })),
  })
}
